import React, { useState, useEffect } from "react";
import Layout from "../../components/layout/Layout";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import { useNavigate, useParams } from "react-router-dom";
import { nowMealsApi } from "../../api/nowMeals";
import { itemCategoriesApi } from "../../api/itemCategories";
import { sideItemsApi } from "../../api/sideItems";
import { sharedStockGroupsApi } from "../../api/sharedStockGroups";
import { catalogueMealPayload } from "../../utils/catalogueMealPayload";
import useAuth from "../../hooks/useAuth";
import { uploadApi } from "../../api/upload";
import { availabilityRulesApi } from "../../api/availabilityRules";
import AddonsDrawer from "../../components/kinnow/AddonsDrawer";
import Swal from "sweetalert2";

const DAYS_MAP = [
  { day: 1, label: "Mon" },
  { day: 2, label: "Tue" },
  { day: 3, label: "Wed" },
  { day: 4, label: "Thu" },
  { day: 5, label: "Fri" },
  { day: 6, label: "Sat" },
  { day: 7, label: "Sun" },
];

const EditNowMeal = () => {
  const { admin } = useAuth();
  const canEditPrices = admin?.role === "admin" || admin?.role === "super-admin";
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState<any[]>([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState<any>({
    name: "",
    description: "",
    image: "",
    categoryId: "",
    currentPrice: 0,
    mrp: 0,
    prepTimeMins: 10,
    nutritionalInfo: {
      calories: 0,
      protein: 0,
      carbs: 0,
      fats: 0,
      fibre: 0,
      magnesium: 0,
      vitaminsAndMinerals: "",
    },
    isVegan: false,
    isGlutenFree: false,
    containsNuts: false,
    containsDairy: false,
    isBestSeller: false,
    isTopPick: false,
    isNewItem: false,
    tags: "",
    ingredients: "",
    displayOrder: 0,
    sharedStockGroupId: "",
    isAddOn: false,
    maxPerOrder: 2,
    isActive: true,
    isAvailable: true,
    currentStock: null as number | null,
  });

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sideItems, setSideItems] = useState<any[]>([]);
  const [allNowMeals, setAllNowMeals] = useState<any[]>([]);
  const [sharedStockGroups, setSharedStockGroups] = useState<any[]>([]);

  // Availability Rule state
  const [existingRuleId, setExistingRuleId] = useState<string | null>(null);
  const [ruleEnabled, setRuleEnabled] = useState(false);
  const [existingRules, setExistingRules] = useState<any[]>([]);
  const [copyRuleId, setCopyRuleId] = useState("");
  const [ruleScheduleType, setRuleScheduleType] = useState<"WEEKLY" | "SPECIFIC_DATES" | "DATE_RANGE">("WEEKLY");
  const [ruleDaysOfWeek, setRuleDaysOfWeek] = useState<number[]>([1, 2, 3, 4, 5]);
  const [ruleSpecificDates, setRuleSpecificDates] = useState("");
  const [ruleStartDate, setRuleStartDate] = useState("");
  const [ruleEndDate, setRuleEndDate] = useState("");
  const [ruleStockMode, setRuleStockMode] = useState<"LIMITED" | "UNLIMITED">("LIMITED");
  const [ruleDefaultQuantity, setRuleDefaultQuantity] = useState(20);
  const [rulePriority, setRulePriority] = useState(10);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catRes, sidesRes, mealsRes, rulesRes, groupsRes] = await Promise.all([
          itemCategoriesApi.getCategories(),
          sideItemsApi.getSideItems(),
          nowMealsApi.getNowMeals({ limit: 1000 }),
          availabilityRulesApi.getRules(),
          sharedStockGroupsApi.getAll(),
        ]);
        setCategories(catRes.categories || []);
        setSideItems(sidesRes.sides || []);
        setAllNowMeals(mealsRes.nowMeals || []);
        setExistingRules(rulesRes.rules || []);
        if (groupsRes.success) setSharedStockGroups(groupsRes.groups);

        if (id) {
          const [mealRes, mealRulesRes] = await Promise.all([
            nowMealsApi.getNowMealById(id),
            availabilityRulesApi.getRules({ mealId: id }),
          ]);
          const meal = mealRes.nowMeal;
          setFormData({
            ...meal,
            categoryId: meal.categoryId?._id || meal.categoryId,
            tags: meal.tags ? meal.tags.join(", ") : "",
            ingredients: meal.ingredients ? meal.ingredients.join(", ") : "",
            nutritionalInfo: {
              ...meal.nutritionalInfo,
              vitaminsAndMinerals: meal.nutritionalInfo?.vitaminsAndMinerals?.join(", ") || "",
            },
            requiredSide: meal.requiredSide ? {
              enabled: meal.requiredSide.enabled || false,
              options: meal.requiredSide.options ? meal.requiredSide.options.map((o: any) => o._id || o) : [],
              defaultSideId: meal.requiredSide.defaultSideId?._id || meal.requiredSide.defaultSideId || ""
            } : { enabled: false, options: [], defaultSideId: "" },
            suggestedSides: meal.suggestedSides ? meal.suggestedSides.map((s: any) => s._id || s) : [],
            pairedMeals: meal.pairedMeals ? meal.pairedMeals.map((p: any) => p._id || p) : []
          });

          // Check if this meal already has an availability rule
          if (mealRulesRes.rules && mealRulesRes.rules.length > 0) {
            const rule = mealRulesRes.rules[0];
            setExistingRuleId(rule._id);
            setRuleEnabled(true);
            setRuleScheduleType(rule.scheduleType);
            setRuleDaysOfWeek(rule.daysOfWeek || []);
            setRuleSpecificDates((rule.specificDates || []).join(", "));
            setRuleStartDate(rule.startDate || "");
            setRuleEndDate(rule.endDate || "");
            setRuleStockMode(rule.stockMode);
            setRuleDefaultQuantity(rule.defaultQuantity || 20);
            setRulePriority(rule.priority || 10);
          }
        }
      } catch (error) {
        setError("Could not load meal details");
        Swal.fire("Error", "Could not load meal details", "error");
      } finally {
        setLoading(false);
      }
    };
    void fetchData();
  }, [id]);

  const handleCopyRule = (ruleId: string) => {
    setCopyRuleId(ruleId);
    const source = existingRules.find((r) => r._id === ruleId);
    if (!source) return;

    setRuleScheduleType(source.scheduleType);
    setRuleDaysOfWeek(source.daysOfWeek || []);
    setRuleSpecificDates((source.specificDates || []).join(", "));
    setRuleStartDate(source.startDate || "");
    setRuleEndDate(source.endDate || "");
    setRuleStockMode(source.stockMode);
    setRuleDefaultQuantity(source.defaultQuantity || 20);
    setRulePriority(source.priority || 10);
    setRuleEnabled(true);
    Swal.fire({
      title: "Rule Copied!",
      text: `Applied schedule settings from "${source.ruleName || source.mealId?.name}"`,
      icon: "success",
      timer: 1500,
      showConfirmButton: false,
    });
  };

  const handleToggleRuleDay = (day: number) => {
    setRuleDaysOfWeek((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day].sort()
    );
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];

    setUploadingImage(true);
    try {
      const response = await uploadApi.uploadImage(file);
      setFormData({ ...formData, image: response.imageUrl });
      Swal.fire("Uploaded", "Image uploaded successfully!", "success");
    } catch (error) {
      Swal.fire("Error", "Failed to upload image", "error");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;

    if (name.startsWith("nutritionalInfo.")) {
      const field = name.split(".")[1];
      setFormData((prev: any) => ({
        ...prev,
        nutritionalInfo: {
          ...prev.nutritionalInfo,
          [field]: type === "number" ? (value === "" ? "" : Number(value)) : value,
        },
      }));
    } else {
      setFormData((prev: any) => ({
        ...prev,
        [name]: type === "number" ? (value === "" ? "" : Number(value)) : value,
      }));
    }
  };

  const handleToggle = (name: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev: any) => ({ ...prev, [name]: e.target.checked }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      setError(null);
      const tagsArray = formData.tags ? formData.tags.split(",").map((t) => t.trim()) : [];
      const ingredientsArray = formData.ingredients
        ? formData.ingredients.split(",").map((t) => t.trim())
        : [];
      const vitsArray = formData.nutritionalInfo.vitaminsAndMinerals
        ? formData.nutritionalInfo.vitaminsAndMinerals.split(",").map((t) => t.trim())
        : [];

      const payload = {
        ...catalogueMealPayload(formData, canEditPrices),
        ...(canEditPrices ? {
          currentPrice: formData.currentPrice === "" ? 0 : Number(formData.currentPrice),
          mrp: formData.mrp === "" ? 0 : Number(formData.mrp),
        } : {}),
        prepTimeMins: formData.prepTimeMins === "" ? 10 : Number(formData.prepTimeMins),
        displayOrder: formData.displayOrder === "" ? 0 : Number(formData.displayOrder),
        sharedStockGroupId: formData.sharedStockGroupId,
        maxPerOrder: formData.maxPerOrder === "" ? 2 : Number(formData.maxPerOrder),
        tags: tagsArray,
        ingredients: ingredientsArray,
        nutritionalInfo: {
          ...formData.nutritionalInfo,
          calories: formData.nutritionalInfo.calories === "" ? 0 : Number(formData.nutritionalInfo.calories),
          protein: formData.nutritionalInfo.protein === "" ? 0 : Number(formData.nutritionalInfo.protein),
          carbs: formData.nutritionalInfo.carbs === "" ? 0 : Number(formData.nutritionalInfo.carbs),
          fats: formData.nutritionalInfo.fats === "" ? 0 : Number(formData.nutritionalInfo.fats),
          fibre: formData.nutritionalInfo.fibre === "" ? 0 : Number(formData.nutritionalInfo.fibre),
          magnesium: formData.nutritionalInfo.magnesium === "" ? 0 : Number(formData.nutritionalInfo.magnesium),
          vitaminsAndMinerals: vitsArray,
        },
        requiredSide: formData.requiredSide ? {
          enabled: formData.requiredSide.enabled,
          options: formData.requiredSide.options || [],
          defaultSideId: formData.requiredSide.defaultSideId === "" ? null : formData.requiredSide.defaultSideId,
        } : { enabled: false, options: [], defaultSideId: null }
      };

      if (id) {
        await nowMealsApi.updateNowMeal(id, payload);

        if (ruleEnabled) {
          const rulePayload: any = {
            mealId: id,
            ruleName: `${formData.name || "Meal"} - ${ruleScheduleType}`,
            scheduleType: ruleScheduleType,
            daysOfWeek: ruleScheduleType === "WEEKLY" ? ruleDaysOfWeek : [],
            specificDates:
              ruleScheduleType === "SPECIFIC_DATES"
                ? ruleSpecificDates
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean)
                : [],
            startDate: ruleScheduleType === "DATE_RANGE" ? ruleStartDate : null,
            endDate: ruleScheduleType === "DATE_RANGE" ? ruleEndDate : null,
            stockMode: ruleStockMode,
            defaultQuantity: ruleDefaultQuantity,
            priority: rulePriority,
          };

          if (existingRuleId) {
            await availabilityRulesApi.updateRule(existingRuleId, rulePayload);
          } else {
            await availabilityRulesApi.createRule(rulePayload);
          }
        }

        Swal.fire("Updated!", "Instant Meal and availability rule updated successfully", "success").then(() => {
          navigate("/now-meals");
        });
      }
    } catch (error: any) {
      setError(error.response?.data?.message || "Failed to update meal");
      Swal.fire("Error", error.response?.data?.message || "Failed to update meal", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" p={5}><CircularProgress /></Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box>
        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {error}
          </Alert>
        )}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" fontWeight="bold">
            Edit Instant Meal
          </Typography>
          <Button variant="outlined" onClick={() => navigate("/now-meals")}>
            Cancel
          </Button>
        </Box>

        <form onSubmit={handleSubmit}>
          <Box display="flex" flexDirection={{ xs: "column", md: "row" }} gap={3}>
            <Box flex={2}>
              <Card sx={{ borderRadius: 3, mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom fontWeight="bold">
                    Core Information
                  </Typography>
                  <Box display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={2}>
                    <Box>
                      <TextField label="Meal Name" name="name" fullWidth required value={formData.name} onChange={handleChange} />
                    </Box>
                    <Box>
                      <TextField select label="Category" name="categoryId" fullWidth required value={formData.categoryId} onChange={handleChange}>
                        {categories.map((c) => <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>)}
                      </TextField>
                    </Box>
                    <Box>
                      <TextField label="Display Order" name="displayOrder" type="number" fullWidth value={formData.displayOrder} onChange={handleChange} />
                    </Box>
                    <Box>
                      <FormControl fullWidth>
                        <InputLabel>Shared Stock Group (Optional)</InputLabel>
                        <Select
                          name="sharedStockGroupId"
                          value={formData.sharedStockGroupId || ""}
                          onChange={(e) => setFormData({...formData, sharedStockGroupId: e.target.value})}
                          label="Shared Stock Group (Optional)"
                        >
                          <MenuItem value=""><em>None</em></MenuItem>
                          {sharedStockGroups.map((group) => (
                            <MenuItem key={group._id} value={group._id}>
                              {group.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                    <Box gridColumn={{ xs: "span 1", sm: "span 2" }}>
                      <TextField label="Description" name="description" fullWidth multiline rows={3} value={formData.description} onChange={handleChange} />
                    </Box>
                    <Box gridColumn={{ xs: "span 1", sm: "span 2" }}>
                      <TextField
                        label="Ingredients (comma separated)"
                        name="ingredients"
                        fullWidth
                        multiline
                        rows={2}
                        value={formData.ingredients}
                        onChange={handleChange}
                      />
                    </Box>
                    <Box gridColumn={{ xs: "span 1", sm: "span 2" }}>
                      <Box border="1px dashed #ccc" p={2} borderRadius={2} textAlign="center">
                        <Typography variant="subtitle2" gutterBottom>Meal Image</Typography>
                        {formData.image && (
                          <img src={formData.image} alt="Preview" style={{ maxHeight: 150, objectFit: "contain", marginBottom: 10, borderRadius: 8 }} />
                        )}
                        <Box>
                          <Button variant="contained" component="label" disabled={uploadingImage}>
                            {uploadingImage ? <CircularProgress size={24} color="inherit" /> : "Upload New Image"}
                            <input type="file" hidden accept="image/*" onChange={handleImageUpload} />
                          </Button>
                        </Box>
                        <TextField name="image" fullWidth size="small" sx={{ mt: 1 }} value={formData.image} onChange={handleChange} required />
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              <Card sx={{ borderRadius: 3, mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom fontWeight="bold">Nutrition (Optional)</Typography>
                  <Box display="grid" gridTemplateColumns={{ xs: "repeat(3, 1fr)", sm: "repeat(6, 1fr)" }} gap={2}>
                    <Box><TextField label="Calories" name="nutritionalInfo.calories" type="number" fullWidth value={formData.nutritionalInfo.calories} onChange={handleChange} /></Box>
                    <Box><TextField label="Protein" name="nutritionalInfo.protein" type="number" fullWidth value={formData.nutritionalInfo.protein} onChange={handleChange} /></Box>
                    <Box><TextField label="Carbs" name="nutritionalInfo.carbs" type="number" fullWidth value={formData.nutritionalInfo.carbs} onChange={handleChange} /></Box>
                    <Box><TextField label="Fats" name="nutritionalInfo.fats" type="number" fullWidth value={formData.nutritionalInfo.fats} onChange={handleChange} /></Box>
                    <Box><TextField label="Fibre" name="nutritionalInfo.fibre" type="number" fullWidth value={formData.nutritionalInfo.fibre} onChange={handleChange} /></Box>
                    <Box><TextField label="Magnesium" name="nutritionalInfo.magnesium" type="number" fullWidth value={formData.nutritionalInfo.magnesium} onChange={handleChange} /></Box>
                    <Box gridColumn={{ xs: "span 3", sm: "span 6" }}><TextField label="Vitamins & Minerals (Comma separated)" name="nutritionalInfo.vitaminsAndMinerals" fullWidth value={formData.nutritionalInfo.vitaminsAndMinerals} onChange={handleChange} /></Box>
                  </Box>
                </CardContent>
              </Card>

              {/* Availability Rule Section */}
              <Card sx={{ borderRadius: 3, mb: 3, border: ruleEnabled ? "1.5px solid" : "1px solid", borderColor: ruleEnabled ? "primary.main" : "divider" }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="h6" fontWeight="bold" display="flex" alignItems="center" gap={1}>
                        <EventRepeatIcon color="primary" /> Daily Availability Rule
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {existingRuleId ? "This dish has an active recurring availability rule." : "Automate when this meal appears in the daily menu and its daily sellable capacity."}
                      </Typography>
                    </Box>
                    <Switch
                      checked={ruleEnabled}
                      onChange={(e) => setRuleEnabled(e.target.checked)}
                      color="primary"
                    />
                  </Box>

                  {ruleEnabled && (
                    <Box sx={{ mt: 2.5, pt: 2, borderTop: "1px solid", borderColor: "divider" }}>
                      {existingRules.length > 0 && (
                        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                          <InputLabel>Copy Schedule From Another Meal</InputLabel>
                          <Select
                            label="Copy Schedule From Another Meal"
                            value={copyRuleId}
                            onChange={(e) => handleCopyRule(e.target.value)}
                            MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                          >
                            <MenuItem value="">-- Select a meal to copy rule from --</MenuItem>
                            {existingRules
                              .filter((r) => r._id !== existingRuleId)
                              .map((r) => (
                                <MenuItem key={r._id} value={r._id}>
                                  {r.mealId?.name} ({r.scheduleType} · {r.stockMode === "UNLIMITED" ? "Unlimited" : `${r.defaultQuantity} units`})
                                </MenuItem>
                              ))}
                          </Select>
                        </FormControl>
                      )}

                      <Box display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={2} sx={{ mb: 2 }}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Schedule Type</InputLabel>
                          <Select
                            label="Schedule Type"
                            value={ruleScheduleType}
                            onChange={(e) => setRuleScheduleType(e.target.value as any)}
                          >
                            <MenuItem value="WEEKLY">Weekly Recurring Days</MenuItem>
                            <MenuItem value="DATE_RANGE">Date Range</MenuItem>
                            <MenuItem value="SPECIFIC_DATES">Specific Dates</MenuItem>
                          </Select>
                        </FormControl>

                        <FormControl fullWidth size="small">
                          <InputLabel>Stock Mode</InputLabel>
                          <Select
                            label="Stock Mode"
                            value={ruleStockMode}
                            onChange={(e) => setRuleStockMode(e.target.value as any)}
                          >
                            <MenuItem value="LIMITED">Limited Stock (Fixed Units)</MenuItem>
                            <MenuItem value="UNLIMITED">Unlimited / Untracked</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>

                      {ruleScheduleType === "WEEKLY" && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1, fontWeight: 600 }}>
                            Active Days of Week:
                          </Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap">
                            {DAYS_MAP.map(({ day, label }) => {
                              const isSelected = ruleDaysOfWeek.includes(day);
                              return (
                                <Chip
                                  key={day}
                                  label={label}
                                  clickable
                                  color={isSelected ? "primary" : "default"}
                                  variant={isSelected ? "filled" : "outlined"}
                                  onClick={() => handleToggleRuleDay(day)}
                                  sx={{ fontWeight: isSelected ? 700 : 500 }}
                                />
                              );
                            })}
                          </Stack>
                        </Box>
                      )}

                      {ruleScheduleType === "DATE_RANGE" && (
                        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                          <TextField
                            label="Start Date"
                            type="date"
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            value={ruleStartDate}
                            onChange={(e) => setRuleStartDate(e.target.value)}
                          />
                          <TextField
                            label="End Date"
                            type="date"
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            value={ruleEndDate}
                            onChange={(e) => setRuleEndDate(e.target.value)}
                          />
                        </Stack>
                      )}

                      {ruleScheduleType === "SPECIFIC_DATES" && (
                        <TextField
                          label="Specific Dates (comma-separated YYYY-MM-DD)"
                          placeholder="2026-09-25, 2026-10-02"
                          fullWidth
                          size="small"
                          value={ruleSpecificDates}
                          onChange={(e) => setRuleSpecificDates(e.target.value)}
                          sx={{ mb: 2 }}
                        />
                      )}

                      {ruleStockMode === "LIMITED" && (
                        <TextField
                          label="Daily Sellable Capacity"
                          type="number"
                          fullWidth
                          size="small"
                          value={ruleDefaultQuantity}
                          onChange={(e) => setRuleDefaultQuantity(Number(e.target.value))}
                        />
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Box>

            <Box flex={1}>
              <Card sx={{ borderRadius: 3, mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom fontWeight="bold">Pricing & Config</Typography>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography>Active</Typography>
                    <Switch checked={Boolean(formData.isActive)} onChange={handleToggle("isActive")} inputProps={{ "aria-label": "Active" }} />
                  </Box>
                  <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                    <Box><TextField label="Current Price" name="currentPrice" type="number" required fullWidth disabled={!canEditPrices} value={formData.currentPrice} onChange={handleChange} /></Box>
                    <Box><TextField label="MRP" name="mrp" type="number" required fullWidth disabled={!canEditPrices} value={formData.mrp} onChange={handleChange} /></Box>
                    <Box><TextField label="Prep Time (mins)" name="prepTimeMins" type="number" fullWidth value={formData.prepTimeMins} onChange={handleChange} /></Box>
                  </Box>

                  <Divider sx={{ my: 3 }} />
                  <Typography variant="subtitle1" fontWeight="bold">Dietary Flags</Typography>
                  <Box display="flex" justifyContent="space-between" mt={1}><Typography>Vegan</Typography><Switch checked={formData.isVegan} onChange={handleToggle("isVegan")} /></Box>
                  <Box display="flex" justifyContent="space-between"><Typography>Gluten Free</Typography><Switch checked={formData.isGlutenFree} onChange={handleToggle("isGlutenFree")} /></Box>
                  <Box display="flex" justifyContent="space-between"><Typography>Contains Nuts</Typography><Switch checked={formData.containsNuts} onChange={handleToggle("containsNuts")} /></Box>
                  <Box display="flex" justifyContent="space-between"><Typography>Contains Dairy</Typography><Switch checked={formData.containsDairy} onChange={handleToggle("containsDairy")} /></Box>

                  <Divider sx={{ my: 3 }} />
                  <Typography variant="subtitle1" fontWeight="bold">Discovery Badges</Typography>
                  <Box display="flex" justifyContent="space-between" mt={1}><Typography>Best Seller</Typography><Switch checked={formData.isBestSeller} onChange={handleToggle("isBestSeller")} /></Box>
                  <Box display="flex" justifyContent="space-between"><Typography>Top Pick</Typography><Switch checked={formData.isTopPick} onChange={handleToggle("isTopPick")} /></Box>
                  <Box display="flex" justifyContent="space-between"><Typography>New Item</Typography><Switch checked={formData.isNewItem} onChange={handleToggle("isNewItem")} /></Box>
                  <TextField label="Tags (comma separated)" name="tags" fullWidth size="small" sx={{ mt: 2 }} value={formData.tags} onChange={handleChange} />
                </CardContent>
              </Card>

              <Button
                variant="outlined"
                color="primary"
                fullWidth
                startIcon={<SettingsIcon />}
                onClick={() => setDrawerOpen(true)}
                sx={{ mb: 3, py: 1.2, borderRadius: 2, fontWeight: "bold" }}
              >
                Configure Add-ons & Pairings
              </Button>
 
              <Button type="submit" variant="contained" size="large" fullWidth disabled={saving} sx={{ py: 1.5, borderRadius: 2, fontSize: "1.1rem" }}>
                {saving ? <CircularProgress size={24} color="inherit" /> : "Save Changes"}
              </Button>
            </Box>
          </Box>
        </form>

        <AddonsDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          formData={formData}
          onChange={setFormData}
          sideItems={sideItems}
          meals={allNowMeals}
          currentMealId={id}
        />
      </Box>
    </Layout>
  );
};

export default EditNowMeal;
